import { useEffect, useState, useRef, useCallback } from "react";
import * as ShipConstants from '../../Global/Constants';
import { getSSECoordinator, supportsSharedSSE } from './sseTabCoordinator';

// Helper to build SSE URL with token
const buildSSEUrl = (endpoint, accessToken) => {
  const baseUrl = ShipConstants.API_URL.replace(/\/$/, '');
  const url = `${baseUrl}/${endpoint.replace(/^\//, '')}?access_token=${accessToken}`;
  return url;
};

export const useNotificationSSE = (accessToken, onMessage, onError) => {
  const [status, setStatus] = useState("disconnected");
  const sourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10;
  const baseReconnectDelay = 1000;
  const useSharedRef = useRef(supportsSharedSSE());
  const onMessageRef = useRef(onMessage);
  const onErrorRef = useRef(onError);

  onMessageRef.current = onMessage;
  onErrorRef.current = onError;

  const getReconnectDelay = useCallback(() => {
    const delay = Math.min(
      baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current),
      30000
    );
    return delay;
  }, []);

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current.close();
      sourceRef.current = null;
    }
  }, []);

  const updateStatus = useCallback((nextStatus) => {
    if (useSharedRef.current) {
      getSSECoordinator().relayStatus(nextStatus);
      return;
    }
    setStatus(nextStatus);
  }, []);

  const deliverMessage = useCallback((payload) => {
    // Always deliver locally first. BroadcastChannel does not echo to the sender,
    // so the leader must handle the message itself and still relay to followers.
    if (onMessageRef.current) {
      onMessageRef.current(payload);
    }
    if (useSharedRef.current) {
      getSSECoordinator().relayMessage(payload);
    }
  }, []);

  const initializeConnectionRef = useRef(null);
  const attemptReconnectRef = useRef(null);

  const attemptReconnect = useCallback(() => {
    if (useSharedRef.current && !getSSECoordinator().isLeader()) {
      return;
    }

    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      updateStatus("failed");
      if (onErrorRef.current) {
        onErrorRef.current(new Error("Max reconnection attempts reached"));
      }
      return;
    }

    const delay = getReconnectDelay();
    reconnectAttemptsRef.current += 1;
    
    reconnectTimeoutRef.current = setTimeout(() => {
      if (accessToken && initializeConnectionRef.current) {
        updateStatus("reconnecting");
        initializeConnectionRef.current();
      }
    }, delay);
  }, [accessToken, getReconnectDelay, updateStatus]);

  attemptReconnectRef.current = attemptReconnect;

  const initializeConnection = useCallback(() => {
    if (!accessToken) {
      updateStatus("disconnected");
      return;
    }

    if (useSharedRef.current && !getSSECoordinator().isLeader()) {
      return;
    }

    try {
      updateStatus("connecting");
      const endpoint = process.env.REACT_APP_SSE_ENDPOINT || 'sse/stream';
      const url = buildSSEUrl(endpoint, accessToken);
      
      const source = new EventSource(url);
      sourceRef.current = source;

      source.onopen = () => {
        updateStatus("connected");
        reconnectAttemptsRef.current = 0;
        console.log("SSE connected successfully", url);
      };

      source.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          deliverMessage({ type: "MESSAGE", payload: data });
        } catch (e) {
          console.error("Error parsing SSE message:", e);
        }
      };

      source.addEventListener("reminder-event", (event) => {
        try {
          const data = JSON.parse(event.data);
          deliverMessage({ event: "reminder-event", data: data });
        } catch (e) {
          console.error("Error parsing reminder-event:", e);
        }
      });

      source.addEventListener("mail-notification-event", (event) => {
        try {
          const data = JSON.parse(event.data);
          deliverMessage({ event: "mail-notification-event", data: data });
        } catch (e) {
          console.error("Error parsing mail-notification-event:", e);
        }
      });

      source.addEventListener("mail", (event) => {
        try {
          const data = JSON.parse(event.data);
          deliverMessage({ type: "MAIL", payload: data });
        } catch (e) {
          console.error("Error parsing mail event:", e);
        }
      });

      source.addEventListener("notification", (event) => {
        try {
          const data = JSON.parse(event.data);
          deliverMessage({ type: "NOTIFICATION", payload: data });
        } catch (e) {
          console.error("Error parsing notification event:", e);
        }
      });

      source.onerror = (err) => {
        console.error("SSE error:", err);
        
        if (source.readyState === EventSource.CLOSED) {
          updateStatus("disconnected");
          cleanup();
          
          if (
            accessToken &&
            reconnectAttemptsRef.current < maxReconnectAttempts &&
            (!useSharedRef.current || getSSECoordinator().isLeader())
          ) {
            if (attemptReconnectRef.current) {
              attemptReconnectRef.current();
            }
          } else {
            updateStatus("failed");
            if (onErrorRef.current) {
              onErrorRef.current(err);
            }
          }
        } else if (source.readyState === EventSource.CONNECTING) {
          updateStatus("connecting");
        }
      };

    } catch (error) {
      console.error("Error initializing SSE connection:", error);
      updateStatus("error");
      if (onErrorRef.current) {
        onErrorRef.current(error);
      }
    }
  }, [accessToken, cleanup, deliverMessage, updateStatus]);

  initializeConnectionRef.current = initializeConnection;

  useEffect(() => {
    if (!accessToken) {
      setStatus("disconnected");
      cleanup();
      if (useSharedRef.current) {
        getSSECoordinator().release();
      }
      return undefined;
    }

    if (useSharedRef.current) {
      const coordinator = getSSECoordinator();

      coordinator.init({
        onRelay: (payload) => {
          if (onMessageRef.current) {
            onMessageRef.current(payload);
          }
        },
        onStatus: (nextStatus) => {
          setStatus(nextStatus);
        },
        onBecomeLeader: () => {
          if (sourceRef.current && sourceRef.current.readyState !== EventSource.CLOSED) {
            return;
          }
          console.log('Initializing shared SSE connection (leader tab)...');
          initializeConnection();
        },
        onBecomeFollower: (lastStatus) => {
          cleanup();
          setStatus(lastStatus || 'disconnected');
        },
      });

      return () => {
        console.log('Cleaning up shared SSE coordinator...');
        cleanup();
        coordinator.release();
      };
    }

    if (sourceRef.current && sourceRef.current.readyState !== EventSource.CLOSED) {
      console.warn('SSE connection already exists, skipping duplicate connection');
      return undefined;
    }

    console.log('Initializing SSE connection...');
    initializeConnection();

    return () => {
      console.log('Cleaning up SSE connection...');
      cleanup();
    };
  }, [accessToken, initializeConnection, cleanup]);

  return status;
};


